import React from 'react'
import { StatusCategory } from '_gql'

import { FormattedCurrency, FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_SIZE_SMALL, BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import { columnSizing, createTagColumn, createTextColumn } from '@divvy-web/skylab.datagridnext'
import { createColumnHelper } from '@divvy-web/skylab.tanstack-react-table'

import { formatDate, getDashboardStatusTagProps } from '@sputnik/pages/DashboardPages/utils'

import { GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge } from './GetCreditApplications.gql'
import styles from './styles.module.scss'

const fillColumnSizing = columnSizing.fill(200)

type CreditApplicationEdge =
  GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge

const columnHelper = createColumnHelper<CreditApplicationEdge>()

// TODO: figure out if this mapping contains all statuses that need mapped

interface ColumnConfigurationParams {
  onViewDetailsClick: (salesforceCreditId: string) => void
}

export const getColumnConfiguration = ({ onViewDetailsClick }: ColumnConfigurationParams) => [
  createTextColumn(columnHelper, 'node.businessInfo.legalBusinessName', {
    id: 'business-name',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Company name'
        id='sputnik.dataGrid__FPGwAt'
      />
    ),
    getTextValue: (row) => row?.original.node?.businessInfo?.legalBusinessName,
    ...fillColumnSizing,
  }),
  createTagColumn(columnHelper, 'node.statusCategory', {
    id: 'status',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Status'
        id='sputnik.dataGrid__tzMNF3'
      />
    ),
    options: {
      [StatusCategory.IN_REVIEW]: getDashboardStatusTagProps(StatusCategory.IN_REVIEW),
      [StatusCategory.INCOMPLETE]: getDashboardStatusTagProps(StatusCategory.INCOMPLETE),
      [StatusCategory.EXPIRES]: getDashboardStatusTagProps(StatusCategory.EXPIRES),
      [StatusCategory.ACTION_NEEDED]: getDashboardStatusTagProps(StatusCategory.ACTION_NEEDED),
      [StatusCategory.ACCOUNT_CREATED]: getDashboardStatusTagProps(StatusCategory.ACCOUNT_CREATED),
      [StatusCategory.DECLINED]: getDashboardStatusTagProps(StatusCategory.DECLINED),
    },
    ...fillColumnSizing,
  }),
  createTextColumn(columnHelper, 'node.startedAt', {
    id: 'started-at',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Started on'
        id='sputnik.dataGrid__Z5cU8c'
      />
    ),
    getTextValue: (row) => formatDate(row?.original.node?.startedAt),
    ...fillColumnSizing,
  }),
  createTextColumn(columnHelper, 'node.shouldBeExpiredAt', {
    id: 'expires-at',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Expires on'
        id='sputnik.dataGrid__0t4WRs'
      />
    ),
    getTextValue: (row) => formatDate(row?.original.node?.shouldBeExpiredAt),
    ...fillColumnSizing,
  }),
  createTextColumn(columnHelper, 'node.fundableLimit', {
    id: 'line-of-credit',
    enableSorting: true,
    header: () => (
      <div className={styles['line-of-credit-column-header']}>
        <FormattedMessage
          defaultMessage='Line of credit'
          id='sputnik.dataGrid__uYh9fa'
        />
      </div>
    ),
    cell: (row) => {
      return (
        <div className={styles['line-of-credit-column']}>
          {!row.getValue() ? '—' : <FormattedCurrency value={row.getValue() || 0} />}
        </div>
      )
    },
    ...fillColumnSizing,
  }),
  columnHelper.display({
    id: 'action',
    header: () => (
      <div className={styles['action-column-header']}>
        <FormattedMessage
          defaultMessage='Action'
          id='sputnik.columns__QlsDcr'
        />
      </div>
    ),
    cell: (info) => (
      <div className={styles['action-column']}>
        <BasicButton
          aria-label={`view details ${info.row.original.node?.salesforceCreditId}`}
          size={BASIC_BUTTON_SIZE_SMALL}
          type={BASIC_BUTTON_TYPE_FLAT}
          onClick={() => {
            onViewDetailsClick(info.row.original.node?.salesforceCreditId ?? '')
          }}
        >
          <FormattedMessage
            defaultMessage='View details'
            id='sputnik.columns__MnpUD7'
          />
        </BasicButton>
      </div>
    ),
    ...fillColumnSizing,
  }),
]
